"use client";
import { hasDealStage, isLostStage, isWonDeal } from "@/lib/constants/deal-stages";

import React, { useMemo } from "react";
import { useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import {
  CheckCircle2,
  FileText,
  ListChecks,
  Mail,
  MoveRight,
  NotebookPen,
  Receipt,
  XCircle,
} from "lucide-react";
import { EntityActionBar, type EntityAction } from "@/components/shared/EntityActionBar";
import type { Deal } from "@/lib/types/database/deals";

interface DealDetailActionsBarProps {
  deal: Deal;
  onRefresh?: () => void;
}

/**
 * Actions on a deal, ordered and weighted by what a rep actually does next.
 *
 * This used to render eight identical `variant="outline"` buttons — moving the
 * deal forward looked exactly like marking it lost. Actions now declare intent
 * (`primary` / `positive` / `negative` / `neutral`) and the shared
 * `EntityActionBar` owns the styling, so every detail page that adopts it reads
 * the same way.
 */
export function DealDetailActionsBar({ deal }: DealDetailActionsBarProps) {
  const { openModal } = useGlobalModalStack();

  const actions = useMemo<EntityAction[]>(() => {
    // `deals.stage` is nullable (#2633). A stage-less deal is neither won nor
    // lost, so it reads as OPEN — the same answer the unguarded calls gave at
    // runtime, where `STAGE_SET.includes(undefined)` is false.
    const isWon = hasDealStage(deal.stage) && isWonDeal(deal.stage);
    const isLost = hasDealStage(deal.stage) && isLostStage(deal.stage);
    const isOpenDeal = !isWon && !isLost;

    const items: EntityAction[] = [
      {
        // Advancing the stage is the most common thing a rep does on an open
        // deal, so it is the one filled button in the bar.
        id: "change-stage",
        label: "Change stage",
        icon: MoveRight,
        intent: "primary",
        pinned: true,
        onSelect: () => openModal(MODAL_IDS.deals.changeStage, { dealId: deal.id }),
      },
    ];

    if (isOpenDeal) {
      items.push({
        id: "mark-won",
        label: "Mark won",
        icon: CheckCircle2,
        intent: "positive",
        pinned: true,
        onSelect: () =>
          openModal(MODAL_IDS.deals.markWon, {
            dealId: deal.id,
            currentNotes: deal.notes ?? null,
          }),
      });
    }

    if (!isLost) {
      items.push({
        id: "mark-lost",
        label: "Mark lost",
        icon: XCircle,
        intent: "negative",
        onSelect: () =>
          openModal(MODAL_IDS.deals.markLost, {
            dealId: deal.id,
            currentNotes: deal.notes ?? null,
          }),
      });
    }

    items.push(
      {
        id: "send-email",
        label: "Send email",
        icon: Mail,
        onSelect: () =>
          openModal(MODAL_IDS.messages.compose, {
            recipientType: "company",
            recipientId: deal.company_id,
            context: { dealId: deal.id },
          }),
      },
      {
        id: "create-task",
        label: "Create task",
        icon: ListChecks,
        onSelect: () =>
          openModal(MODAL_IDS.tasks.create, {
            dealId: deal.id,
            prefillData: { title: `Follow up on ${deal.title}` },
          }),
      },
      {
        id: "log-activity",
        label: "Log activity",
        icon: NotebookPen,
        onSelect: () =>
          openModal(MODAL_IDS.messages.logActivity, {
            dealId: deal.id,
            entityType: "deal",
          }),
      }
    );

    // Quote/invoice are meaningless on a lost deal — nothing to sell or bill.
    if (!isLost) {
      const revenue = deal.revenue != null ? Number(deal.revenue) : null;
      const monthly = deal.monthly_amount != null ? Number(deal.monthly_amount) : null;
      const defaultAmount =
        revenue != null && revenue > 0
          ? revenue
          : deal.is_recurring && monthly != null && monthly > 0
            ? monthly
            : null;

      items.push(
        {
          id: "create-quote",
          label: "Create quote",
          icon: FileText,
          title: "AI-drafts a quote from this deal",
          onSelect: () =>
            openModal(MODAL_IDS.deals.createQuote, {
              dealId: deal.id,
              dealTitle: deal.title,
              companyName: deal.company_name ?? null,
            }),
        },
        {
          id: "create-invoice",
          label: "Create invoice",
          icon: Receipt,
          title: "Draft a QuickBooks invoice from this deal",
          onSelect: () =>
            openModal(MODAL_IDS.deals.createInvoice, {
              dealId: deal.id,
              dealTitle: deal.title,
              companyName: deal.company_name ?? null,
              defaultAmount,
            }),
        }
      );
    }

    return items;
  }, [deal, openModal]);

  return <EntityActionBar actions={actions} ariaLabel="Deal actions" />;
}
